import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  IconButton,
  Chip,
  CircularProgress,
  Tooltip,
  Menu,
  MenuItem,
  Alert
} from '@mui/material';
import {
  SmartToy as BotIcon,
  Person as AgentIcon,
  Phone as UserIcon,
  Done as SentIcon,
  DoneAll as DeliveredIcon,
  Error as ErrorIcon,
  Refresh as RetryIcon,
  MoreVert as MoreIcon,
  ContentCopy as CopyIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { WhatsAppMessage } from '../types';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

interface MessageBubbleProps {
  message: WhatsAppMessage;
  isLastMessage?: boolean;
  onRetry?: (message: WhatsAppMessage) => void;
  onCopy?: (content: string) => void;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  isLastMessage = false,
  onRetry,
  onCopy
}) => {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  
  const isOutgoing = message.sender === 'agent' || message.sender === 'ai';
  const isAI = message.sender === 'ai';
  const isFailed = message.status === 'failed';
  const isSending = message.status === 'sending';

  const formatTimestamp = (timestamp: string) => {
    try {
      return formatDistanceToNow(new Date(timestamp), {
        addSuffix: true,
        locale: fr
      });
    } catch {
      return 'À l\'instant';
    }
  };

  const getSenderIcon = () => {
    switch (message.sender) {
      case 'ai':
        return <BotIcon sx={{ fontSize: 16 }} />;
      case 'agent':
        return <AgentIcon sx={{ fontSize: 16 }} />;
      case 'user':
        return <UserIcon sx={{ fontSize: 16 }} />;
      default:
        return null;
    }
  };

  const getStatusIcon = () => {
    if (isSending) {
      return <CircularProgress size={12} />;
    }
    
    switch (message.status) {
      case 'sent':
        return <SentIcon sx={{ fontSize: 12, color: 'text.secondary' }} />;
      case 'delivered':
      case 'read':
        return <DeliveredIcon sx={{ fontSize: 12, color: 'primary.main' }} />;
      case 'failed':
        return (
          <Tooltip title="Échec d'envoi - Cliquer pour réessayer">
            <IconButton 
              size="small" 
              onClick={() => onRetry?.(message)}
              sx={{ p: 0, ml: 0.5 }}
            >
              <ErrorIcon sx={{ fontSize: 12, color: 'error.main' }} />
            </IconButton>
          </Tooltip>
        );
      default:
        return null;
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleCopy = () => {
    onCopy?.(message.content);
    handleMenuClose();
  };

  const handleRetry = () => {
    onRetry?.(message);
    handleMenuClose();
  };

  const renderMediaContent = () => {
    if (message.type === 'text') return null;

    const { metadata } = message;
    
    switch (message.type) {
      case 'image':
        return (
          <Box sx={{ mb: 1 }}>
            <img
              src={metadata?.mediaUrl || '/placeholder-image.jpg'}
              alt="Image partagée"
              style={{
                maxWidth: '100%',
                maxHeight: '200px',
                borderRadius: '8px',
                display: 'block'
              }}
            />
          </Box>
        );
      
      case 'document':
        return (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1, 
            p: 1, 
            bgcolor: 'action.hover', 
            borderRadius: 1,
            mb: 1
          }}>
            <DownloadIcon color="primary" />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" fontWeight="medium">
                {metadata?.fileName || 'Document'}
              </Typography>
              {metadata?.fileSize && (
                <Typography variant="caption" color="text.secondary">
                  {(metadata.fileSize / 1024 / 1024).toFixed(2)} MB
                </Typography>
              )}
            </Box>
          </Box>
        );
      
      default:
        return (
          <Alert severity="info" sx={{ mb: 1 }}>
            Média non supporté: {message.type}
          </Alert>
        );
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isOutgoing ? 'flex-end' : 'flex-start',
        mb: 1,
        px: 1
      }}
    >
      {/* Avatar pour messages entrants */}
      {!isOutgoing && (
        <Avatar sx={{ width: 32, height: 32, mr: 1, bgcolor: 'primary.main' }}>
          <UserIcon sx={{ fontSize: 18 }} />
        </Avatar>
      )}

      {/* Bulle de message */}
      <Paper
        elevation={1}
        sx={{
          maxWidth: '70%',
          minWidth: '120px',
          p: 1.5,
          backgroundColor: isOutgoing 
            ? (isAI ? 'secondary.light' : 'primary.light')
            : 'background.paper',
          color: isOutgoing ? 'primary.contrastText' : 'text.primary',
          borderRadius: 2,
          borderTopRightRadius: isOutgoing ? 0.5 : 2,
          borderTopLeftRadius: isOutgoing ? 2 : 0.5,
          opacity: isFailed ? 0.7 : 1,
          border: isFailed ? '1px solid' : 'none',
          borderColor: isFailed ? 'error.main' : 'transparent'
        }}
      >
        {/* Header avec sender et menu */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          mb: 0.5
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {getSenderIcon()}
            <Typography variant="caption" fontWeight="medium">
              {isAI ? 'IA' : message.sender === 'agent' ? 'Agent' : 'Client'}
            </Typography>
            {isAI && message.agentResponse && (
              <Chip
                size="small"
                label={`${Math.round(message.agentResponse.confidence * 100)}%`}
                color="secondary"
                variant="outlined"
                sx={{ height: 16, fontSize: '0.7rem' }}
              />
            )}
          </Box>
          
          <IconButton 
            size="small" 
            onClick={handleMenuOpen}
            sx={{ p: 0.25, opacity: 0.7 }}
          >
            <MoreIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>

        {/* Contenu média */}
        {renderMediaContent()}

        {/* Contenu texte */}
        <Typography 
          variant="body2" 
          sx={{ 
            wordBreak: 'break-word',
            whiteSpace: 'pre-wrap',
            mb: 0.5
          }}
        >
          {message.content}
        </Typography>

        {/* Footer avec timestamp et statut */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          mt: 0.5
        }}>
          <Typography variant="caption" sx={{ opacity: 0.7 }}>
            {formatTimestamp(message.timestamp)}
          </Typography>
          
          {isOutgoing && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {getStatusIcon()}
              {isFailed && (
                <Tooltip title="Réessayer">
                  <IconButton 
                    size="small" 
                    onClick={() => onRetry?.(message)}
                    sx={{ p: 0 }}
                  >
                    <RetryIcon sx={{ fontSize: 12 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
        </Box>

        {/* Infos supplémentaires pour l'IA */}
        {isAI && message.agentResponse && (
          <Typography variant="caption" sx={{ 
            display: 'block', 
            opacity: 0.6, 
            mt: 0.5,
            fontStyle: 'italic'
          }}>
            Modèle: {message.agentResponse.model} • 
            Temps: {message.agentResponse.processingTime}ms
          </Typography>
        )}
      </Paper>

      {/* Menu contextuel */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleCopy}>
          <CopyIcon sx={{ mr: 1, fontSize: 16 }} />
          Copier
        </MenuItem>
        
        {isFailed && (
          <MenuItem onClick={handleRetry}>
            <RetryIcon sx={{ mr: 1, fontSize: 16 }} />
            Réessayer
          </MenuItem>
        )}
        
        {message.type !== 'text' && message.metadata?.mediaUrl && (
          <MenuItem 
            component="a" 
            href={message.metadata.mediaUrl} 
            download
            onClick={handleMenuClose}
          >
            <DownloadIcon sx={{ mr: 1, fontSize: 16 }} />
            Télécharger
          </MenuItem>
        )}
      </Menu>

      {/* Avatar pour messages sortants */}
      {isOutgoing && (
        <Avatar sx={{ 
          width: 32, 
          height: 32, 
          ml: 1, 
          bgcolor: isAI ? 'secondary.main' : 'primary.main' 
        }}>
          {getSenderIcon()}
        </Avatar>
      )}
    </Box>
  );
};

export default MessageBubble;
